/**
 * bodyMotionPath — pure geometry helpers for the navmesh -> kimodo bridge
 * (no THREE / DOM deps, so they are unit-testable under `node --test`).
 *
 * Used by bodyMotionNav.js to turn a navmesh path into per-frame root2d
 * waypoints and to snap generated root positions back onto the navmesh.
 */

/** Planar XZ distance between two [x,y,z] (or [x,,z]) points. */
export function distXZ(a, b) {
  const dx = a[0] - b[0], dz = a[2] - b[2]
  return Math.hypot(dx, dz)
}

/**
 * Ease-in / ease-out, as fractions of the clip's duration, for the ROOT's speed
 * along the path. 0.35 s at either end — roughly how long a person takes to
 * reach a walking pace from standing, and to stop.
 *
 * The resample is otherwise uniform in arc length, which makes the request a
 * CONSTANT-VELOCITY one: frame 0 already demands the full 1.3 m/s and the last
 * frame demands the character still travelling at it. That is the position-side
 * twin of the heading problem DEFAULT_HEADING_RAMP_FRAMES addresses — a standing
 * character being asked to be already up to speed, already facing along the
 * route, on the very first frame.
 *
 * Total arc length is unchanged, so the route still ends where it ended and the
 * clip is still the same number of frames; only the distribution of frames along
 * it moves.
 */
export const DEFAULT_EASE_IN_SEC = 0.35
export const DEFAULT_EASE_OUT_SEC = 0.35

/**
 * A speed profile over normalised time, and its inverse.
 *
 * Speed rises from 0 over the first `easeIn` of the duration, holds a plateau,
 * then falls back to 0 over the last `easeOut`. The plateau is solved so the
 * area under it is exactly 1 — the whole path is still covered — which is why
 * the plateau EXCEEDS the average: time spent accelerating has to be made up.
 *
 *   V = 1 / (1 - (easeIn + easeOut)/2)
 *
 * The ramps are SMOOTHSTEP, not linear. A linear ramp holds a constant
 * acceleration and then drops it to zero the instant the plateau begins — a step
 * in acceleration, i.e. an unbounded jerk, at each junction, which reads as the
 * ease being abrupt however long it is made. Smoothstep starts and ends its ramp
 * at zero acceleration, so speed joins the plateau tangentially and the start of
 * the clip has no kick either.
 *
 * The plateau maths is unchanged by the swap: smoothstep integrates to 1/2 over
 * a unit interval exactly as a linear ramp does, so a ramp still covers half the
 * ground a full-speed one would and V keeps its old closed form.
 *
 * `easeIn + easeOut` is capped at 1, where the plateau vanishes and the two ramps
 * meet. Past that they would overlap and the piecewise form breaks (it returns
 * arc positions beyond the end of the path).
 *
 * The inverse is needed, not optional: waypointFrameIndices maps a waypoint's arc
 * position back to a FRAME and can only do so through the same profile the
 * resample used. Smoothstep's integral is a quartic with no clean root, so the
 * ramps invert by bisection — monotonic, so it always converges, and it runs at
 * user cadence (once per waypoint) rather than per frame.
 *
 * @param {object} [opts]
 * @param {number} [opts.easeIn=0]   fraction of the duration spent accelerating
 * @param {number} [opts.easeOut=0]  fraction spent decelerating
 * @returns {{at:(t:number)=>number, inverse:(s:number)=>number, plateau:number}}
 */
export function arcProfile({ easeIn = 0, easeOut = 0 } = {}) {
  let a = Math.max(0, easeIn)
  let b = Math.max(0, easeOut)
  const sum = a + b
  const MAX = 1
  if (sum > MAX) { a *= MAX / sum; b *= MAX / sum }
  if (a === 0 && b === 0) {
    return { at: (t) => t, inverse: (s) => s, plateau: 1 }
  }
  const V = 1 / (1 - (a + b) / 2)
  const sAccel = (V * a) / 2       // arc covered by the end of the ramp-up
  const sDecel = 1 - (V * b) / 2   // arc at which the ramp-down begins

  // Integral of smoothstep (3u^2 - 2u^3) over [0, u]; ramp(1) = 1/2.
  const ramp = (u) => u * u * u - (u * u * u * u) / 2
  // Bisection on a monotonic ramp. 60 halvings takes the bracket below 1e-18,
  // so the result is exact to double precision and the loop cannot spin.
  const rampInverse = (target) => {
    let lo = 0, hi = 1
    for (let i = 0; i < 60; i++) {
      const mid = (lo + hi) / 2
      if (ramp(mid) < target) lo = mid; else hi = mid
    }
    return (lo + hi) / 2
  }

  return {
    plateau: V,
    at(t) {
      if (t <= 0) return 0
      if (t >= 1) return 1
      if (a > 0 && t < a) return V * a * ramp(t / a)
      if (b > 0 && t > 1 - b) return 1 - V * b * ramp((1 - t) / b)
      return sAccel + V * (t - a)
    },
    inverse(s) {
      if (s <= 0) return 0
      if (s >= 1) return 1
      if (a > 0 && s < sAccel) return a * rampInverse(s / (V * a))
      if (b > 0 && s > sDecel) return 1 - b * rampInverse((1 - s) / (V * b))
      return a + (s - sAccel) / V
    },
  }
}

/**
 * Resolve the ease fractions for a clip of `durationSec`, from seconds.
 * Returns the profile ready to hand to resamplePathXZ / waypointFrameIndices.
 */
export function speedProfileFor(durationSec, { easeInSec = DEFAULT_EASE_IN_SEC, easeOutSec = DEFAULT_EASE_OUT_SEC } = {}) {
  if (!(durationSec > 0)) return arcProfile()
  return arcProfile({ easeIn: easeInSec / durationSec, easeOut: easeOutSec / durationSec })
}

/**
 * Resample a navmesh polyline path ([[x,y,z], …]) to exactly `n` [x, z]
 * waypoints, one per generation frame. Constant end-points; a degenerate
 * (single-node or zero-length) path repeats node 0.
 *
 * Evenly spaced by ARC LENGTH by default — which makes the resulting request a
 * constant-velocity one. Pass an `arcProfile` to ease the speed in and out
 * instead; the frames move along the path, the path itself does not change.
 *
 * @param {number[][]} path
 * @param {number} n  frame count
 * @param {{at:Function}|null} [profile]  speed profile (see arcProfile)
 */
export function resamplePathXZ(path, n, profile = null) {
  if (!path || path.length === 0) return []
  if (path.length === 1) return Array.from({ length: n }, () => [path[0][0], path[0][2]])
  // Cumulative arc length along the polyline.
  const seg = [0]
  for (let i = 1; i < path.length; i++) seg[i] = seg[i - 1] + distXZ(path[i - 1], path[i])
  const total = seg[seg.length - 1]
  if (total === 0) return Array.from({ length: n }, () => [path[0][0], path[0][2]])

  const out = []
  let j = 0
  for (let f = 0; f < n; f++) {
    // Without a profile this is (total * f) / (n - 1) — uniform arc length, i.e.
    // constant speed. A profile redistributes the SAME frames along the SAME
    // path so the root accelerates and decelerates instead.
    const u = f / (n - 1)
    const target = total * (profile ? profile.at(u) : u)
    while (j < path.length - 2 && seg[j + 1] < target) j++
    const segLen = seg[j + 1] - seg[j] || 1
    const t = Math.min(1, Math.max(0, (target - seg[j]) / segLen))
    const a = path[j], b = path[j + 1]
    out.push([a[0] + (b[0] - a[0]) * t, a[2] + (b[2] - a[2]) * t])
  }
  return out
}

/** Total XZ arc length of a navmesh polyline path ([[x,y,z], …]). */
export function pathLengthXZ(path) {
  if (!path || path.length < 2) return 0
  let total = 0
  for (let i = 1; i < path.length; i++) total += distXZ(path[i - 1], path[i])
  return total
}

/**
 * Route-derived clip duration: route length (m) ÷ avg walk speed (m/s), clamped
 * to [1.5, 15] s. A fixed duration makes short routes creep and foot-skate and
 * long ones sprint; deriving from length keeps the gait matched to the travel.
 * The single source of truth for the clamp — shared by bodyMotionNav (the actual
 * request) and the wizard (the derived-duration display).
 */
export function clampRouteDuration(lengthM, speedMps) {
  return Math.min(15, Math.max(1.5, lengthM / speedMps))
}

/**
 * Per-frame unit tangents [[cos,sin], …] of a resampled [x,z] waypoint list
 * (central difference; one-sided at the ends; a degenerate/stationary segment
 * carries the previous heading, defaulting to +x).
 */
export function pathTangents(xz) {
  const n = xz.length
  const out = []
  let prev = [1, 0]
  for (let f = 0; f < n; f++) {
    const a = xz[Math.max(0, f - 1)], b = xz[Math.min(n - 1, f + 1)]
    const dx = b[0] - a[0], dz = b[1] - a[1]
    const len = Math.hypot(dx, dz)
    if (len > 1e-6) prev = [dx / len, dz / len]
    out.push(prev)
  }
  return out
}

/**
 * Snap a world XZ point onto the nearest navmesh node.
 * @param {{nearestNode:Function, nodes:Array}} nm  a NavMesh instance
 * @returns {{ xz:[number,number], dist:number }} snapped [x,z] + pre-snap drift (m)
 */
export function snapToNavmesh(nm, x, z) {
  const idx = nm.nearestNode([x, 0, z])
  if (idx == null || idx < 0) return { xz: [x, z], dist: Infinity }
  const p = nm.nodes[idx].p
  return { xz: [p[0], p[2]], dist: Math.hypot(p[0] - x, p[2] - z) }
}

/**
 * Resolve a ground-plane click into a waypoint. navmesh mode snaps via
 * snapToNavmesh and THROWS when the click is > maxDrift off the mesh (loud
 * rejection, never a silently unsnapped point); straight mode passes through.
 *
 * @param {object|null} nm  a NavMesh instance (required for mode 'navmesh')
 * @param {number} x  world X of the ground-plane hit
 * @param {number} z  world Z of the ground-plane hit
 * @param {object} [opts]
 * @param {'navmesh'|'straight'} [opts.mode='navmesh']
 * @param {number} [opts.maxDrift=1]  navmesh: reject a click this many metres off the mesh
 * @returns {{ x:number, z:number, snapped:boolean, drift:number }}
 */
export function pickWaypoint(nm, x, z, { mode = 'navmesh', maxDrift = 1 } = {}) {
  if (mode === 'navmesh') {
    if (!nm) throw new Error('pickWaypoint: navmesh required for mode "navmesh"')
    const { xz, dist } = snapToNavmesh(nm, x, z)
    if (dist > maxDrift) throw new Error(`clicked ${dist.toFixed(2)} m off the navmesh (max ${maxDrift} m)`)
    return { x: xz[0], z: xz[1], snapped: true, drift: dist }
  }
  if (mode === 'straight') {
    return { x, z, snapped: false, drift: 0 }
  }
  throw new Error('pickWaypoint: unknown mode ' + mode)
}

/**
 * Route shape modes. 'curve' — the default — rounds every turn of the planned
 * route into a Bézier fillet (smoothRoutePath), leaving the straight runs
 * exactly where the navmesh put them. 'none' is the raw A* polyline, kept as the
 * comparison the wizard's Shape control offers.
 *
 * There was briefly a third, 'pull', which string-pulled the staircase into
 * straight legs by testing long line-of-sight segments against the mesh. It is
 * gone: two separate implementations of that visibility test both cut routes
 * through a live scene, because both had to model what a bake means by walkable
 * and a wrong model fails permissively. Corner-cutting needs no such model —
 * see smoothRoutePath.
 */
export const SMOOTH_MODES = ['none', 'curve']
export const DEFAULT_SMOOTH = 'curve'

/**
 * Metres of each leg rounded away at a turn — the corner radius of the Bézier
 * fillet in smoothRoutePath, and the only control over how round the curve is.
 *
 * It is a radius, not a tolerance: it says how much of each corner is rounded,
 * NOT how far the route may wander, because a fillet's excursion is bounded by
 * its own control triangle (about 0.35 x radius, always toward the inside of the
 * turn). That is the difference from the relaxation this replaced, where the two
 * were the same number and "smoother" therefore meant "further from the route".
 *
 * 0.5 m is roughly how wide a person's own path is when they round a corner at a
 * walk. Straight runs are untouched at any radius.
 */
export const DEFAULT_CORNER_RADIUS_M = 0.5

/**
 * How far the curve may sit from the nearest navmesh node — the slack that lets
 * it leave the grid route, and the one control over "smooth" versus "follows the
 * navmesh". Resolved as min(cells x measured spacing, absolute metres).
 *
 * The trade-off is geometric, not a tuning accident. A 4-connected grid's
 * staircase zigzags by about half a cell, so ironing it out means moving about
 * half a cell sideways; and the straight line a diagonal stands for runs through
 * cell interiors, up to spacing/sqrt(2) = 0.707 cells from any node. Measured on
 * a 0.25 m grid diagonal (total heading variation is the ripple, which is what
 * makes a gait fight the route):
 *
 *   slack   length   worst step   total turn
 *   0.35c   10.61 m     29.6 deg      3221 deg   ripple survives
 *   0.50c    8.71 m     19.0 deg       424 deg
 *   0.71c    8.49 m      0.1 deg        11 deg   the exact straight line
 *
 * So tighter is not simply safer: it keeps the curve near the nodes and keeps
 * the grid artefact with it. 0.5 cells is the knee.
 *
 * The ABSOLUTE cap exists because what reads as "off the navmesh" is metres, not
 * cells: on a coarse bake half a cell can be a stride's width, and the same
 * fraction that is invisible on a 0.25 m grid is glaring on a 1 m one. The
 * wizard exposes this per clip.
 */
export const DEFAULT_CURVE_DRIFT_CELLS = 0.5
export const DEFAULT_CURVE_DRIFT_MAX_M = 0.25

/** Resolve the slack for a mesh: min(cells x spacing, absolute cap). */
export function curveDrift(nm, { driftCells = DEFAULT_CURVE_DRIFT_CELLS, driftMaxM = DEFAULT_CURVE_DRIFT_MAX_M } = {}) {
  if (!nm) return Infinity
  return Math.min(meshSpacing(nm) * driftCells, driftMaxM)
}

/**
 * The mesh's ACTUAL node spacing: the median length of its edges, measured once
 * per NavMesh instance and cached.
 *
 * Every threshold below is a multiple of this rather than of `nm.cellSize`,
 * because a declared cell size that disagrees with the real spacing breaks the
 * clearance test in BOTH directions and neither is visible: too large and holes
 * stop being detected (the line cuts through the scene), too small and every
 * interior point reads as off-mesh (smoothing silently does nothing at all).
 * The graph knows its own spacing, so nothing has to be declared correctly.
 *
 * Median, not mean: a bake with a few long stitching edges across a doorway
 * would drag a mean upward and loosen every threshold at once. Sampled with a
 * fixed stride rather than randomly — a big mesh should not pay for a full sort,
 * and the value must not change between two calls on the same mesh.
 */
const SPACING_CACHE = new WeakMap()
export function meshSpacing(nm) {
  const hit = SPACING_CACHE.get(nm)
  if (hit !== undefined) return hit
  const lens = []
  const nodes = nm.nodes || []
  const adj = nm.adj || []
  const stride = Math.max(1, Math.floor(nodes.length / 512))
  for (let i = 0; i < nodes.length; i += stride) {
    for (const e of adj[i] || []) {
      if (e.to <= i) continue // each undirected edge once
      lens.push(Number.isFinite(e.cost) ? e.cost : distXZ(nodes[i].p, nodes[e.to].p))
    }
  }
  lens.sort((a, b) => a - b)
  // A mesh with no edges has no spacing to measure; fall back to the declared
  // cell so the drift backstop still gets a number.
  const val = lens.length ? lens[lens.length >> 1] : (nm.cellSize > 0 ? nm.cellSize : 0.25)
  SPACING_CACHE.set(nm, val)
  return val
}

/**
 * The vertices where a polyline actually CHANGES DIRECTION, with runs of
 * collinear points merged away.
 *
 * Note what this is not: it never asks whether a shortcut is walkable, so it
 * cannot invent one. A vertex is dropped only when it already lies on the line
 * between the ones either side of it, which is a statement about the polyline
 * and needs no model of the mesh at all. That is the difference between this and
 * the string-pull that had to be abandoned.
 *
 * @param {number[][]} path  [[x,y,z], ...]
 * @param {object} [opts]
 * @param {number} [opts.angleTol=1]  degrees; a turn smaller than this is straight
 * @returns {number[][]} the endpoints plus every turning vertex
 */
export function turningPoints(path, { angleTol = 1 } = {}) {
  if (!Array.isArray(path) || path.length < 3) return path ? path.map(p => p.slice()) : []
  const cos = Math.cos((angleTol * Math.PI) / 180)
  const out = [path[0].slice()]
  for (let i = 1; i < path.length - 1; i++) {
    // Measured from the last KEPT vertex, so a long straight run collapses to
    // one segment instead of surviving as a chain of tiny near-straight steps.
    const prev = out[out.length - 1], cur = path[i], next = path[i + 1]
    const ax = cur[0] - prev[0], az = cur[2] - prev[2]
    const bx = next[0] - cur[0], bz = next[2] - cur[2]
    const la = Math.hypot(ax, az), lb = Math.hypot(bx, bz)
    if (la < 1e-9 || lb < 1e-9) continue // duplicate point: nothing to turn through
    if ((ax * bx + az * bz) / (la * lb) < cos) out.push(cur.slice())
  }
  out.push(path[path.length - 1].slice())
  return out
}

/** Points along the quadratic Bézier P0 -> C -> P2, inclusive of both ends. */
function quadratic(p0, c, p2, n) {
  const pts = []
  for (let i = 0; i <= n; i++) {
    const t = i / n, u = 1 - t, w0 = u * u, w1 = 2 * u * t, w2 = t * t
    pts.push([
      w0 * p0[0] + w1 * c[0] + w2 * p2[0],
      w0 * p0[1] + w1 * c[1] + w2 * p2[1],
      w0 * p0[2] + w1 * c[2] + w2 * p2[2],
    ])
  }
  return pts
}

/**
 * Join a grid path diagonally: drop a vertex whenever the straight line from its
 * predecessor to its successor stays on the navmesh, so a staircase becomes the
 * diagonal it stands for. Runs BEFORE the corner rounding, on the raw A* path.
 *
 * This is a LOCAL string-pull, and locality is the whole point. The two earlier
 * attempts tested one long line of sight across the entire route and both cut
 * paths through a live scene, because a walkability model that is wrong anywhere
 * along a 12 m segment discards every vertex between. Here the span is a single
 * vertex — bounded by `maxSpan` (default two node spacings) — so a wrong answer
 * costs one cell of shortcut, not the route. It also cannot cut a corner the
 * grid did not already go round: the replacement segment spans two steps.
 *
 * Repeated until nothing more merges, which is what turns a run of single steps
 * into one long diagonal rather than a slightly shorter staircase. Endpoints are
 * never dropped. Passes are capped so a pathological mesh cannot spin.
 *
 * @param {object|null} nm  a NavMesh instance; null returns the path unchanged
 *   (there is nothing to test against, and guessing is what this avoids)
 * @param {number[][]} path
 * @param {object} [opts]
 * @param {number} [opts.maxSpan]  longest replacement segment, in metres;
 *   default 2.2 x the measured node spacing (a lattice diagonal is 1.41 x, so
 *   this admits a diagonal step and its immediate merges, not a long shortcut)
 * @param {number} [opts.maxDrift]  how far a sample may sit from any node;
 *   default curveDrift(nm)
 * @param {number} [opts.maxPasses=8]
 * @returns {number[][]}
 */
export function joinDiagonals(nm, path, { maxSpan, maxDrift, maxPasses = 8 } = {}) {
  if (!nm || !Array.isArray(path) || path.length < 3) return path ?? []
  const spacing = meshSpacing(nm)
  const span = maxSpan ?? spacing * 2.2
  const step = Math.max(1e-3, spacing * 0.25)
  // NOT curveDrift here, and this is the crux. A diagonal across a cell passes
  // through the cell's interior, and the middle of a cell is spacing/sqrt(2) =
  // 0.707 spacings from any node — so the rounding stage's tighter bound rejects
  // the exact shortcut this stage exists to take (measured: it merged nothing,
  // 25 vertices to 20, length unchanged). 0.72 admits a genuine cell interior
  // and nothing further.
  const drift = maxDrift ?? spacing * 0.72

  // On its own that bound would also admit a diagonal clipping the corner of an
  // obstacle, so the merge additionally requires the OPPOSITE CORNER of the step
  // to be walkable: for a turn a -> b -> c, the parallelogram completion
  // o = a + c - b is the node on the other side of the diagonal, and cutting the
  // corner is exactly the case where it is missing. This is the standard
  // no-corner-cutting rule for diagonal grid movement, and it is a LOCAL
  // question about one cell rather than a claim about a whole segment.
  const nodeNear = (x, z, tol) => snapToNavmesh(nm, x, z).dist <= tol
  const clear = (a, b, viaB) => {
    const len = distXZ(a, b)
    if (len > span) return false
    if (viaB) {
      const ax = b[0] - a[0], az = b[2] - a[2]
      const bx = viaB[0] - b[0], bz = viaB[2] - b[2]
      void ax; void az; void bx; void bz
    }
    const n = Math.max(1, Math.ceil(len / step))
    for (let i = 0; i <= n; i++) {
      const t = i / n
      const x = a[0] + (b[0] - a[0]) * t
      const z = a[2] + (b[2] - a[2]) * t
      if (!nodeNear(x, z, drift)) return false
    }
    return true
  }

  /** May `b` be dropped from the turn a -> b -> c? */
  const canDrop = (a, b, c) => {
    if (!clear(a, c)) return false
    // Collinear: dropping b only removes a redundant vertex from a straight run,
    // so there is no corner to cut and no opposite node to ask about.
    const ux = b[0] - a[0], uz = b[2] - a[2]
    const vx = c[0] - b[0], vz = c[2] - b[2]
    const cross = Math.abs(ux * vz - uz * vx)
    if (cross < 1e-9) return true
    // The other way round the step must be walkable too.
    return nodeNear(a[0] + c[0] - b[0], a[2] + c[2] - b[2], spacing * 0.35)
  }

  let cur = path
  for (let pass = 0; pass < maxPasses; pass++) {
    if (cur.length < 3) break
    const out = [cur[0]]
    let merged = false
    let i = 1
    while (i < cur.length - 1) {
      const a = out[out.length - 1], b = cur[i], c = cur[i + 1]
      if (canDrop(a, b, c)) {
        // Drop b: a -> c goes straight. Skip c as a merge candidate this pass so
        // one pass cannot chain a whole run into a single long segment behind the
        // span check — the next pass will, one step at a time.
        out.push(c)
        merged = true
        i += 2
      } else {
        out.push(b)
        i += 1
      }
    }
    if (i === cur.length - 1) out.push(cur[cur.length - 1])
    cur = out
    if (!merged) break
  }
  return cur
}

/**
 * Smooth a planned route by rounding each of its TURNS into a Bézier fillet,
 * leaving every straight run exactly where the navmesh put it.
 *
 * At a corner C between legs to A and B, the fillet is the quadratic Bézier
 * whose control point IS the corner and whose endpoints sit `radius` back along
 * the incoming and outgoing legs. So the control net is read off the two lines
 * meeting there, and the curve leaves and rejoins the route tangentially — it
 * departs from the planned path only inside the fillet, and only ever toward the
 * inside of the turn (a quadratic Bézier stays within the triangle of its own
 * control points, so the excursion is at most about 0.35 x radius and can never
 * overshoot outward).
 *
 * This replaced Laplacian relaxation, which was global in effect: on a route
 * where most vertices turn, every vertex moved, and the whole path drifted
 * toward a straight line held back only by a distance-to-mesh test. Filleting is
 * bounded per corner by construction, so "smooth" and "follows the navmesh" stop
 * being in tension — the radius sets how much of each corner is rounded, not how
 * far the route as a whole may wander.
 *
 * SAFETY. The radius is capped at half the shorter adjacent leg, so consecutive
 * fillets can never overlap or reorder the route. Each candidate fillet is then
 * sampled and checked against the mesh; a fillet that would leave it is retried
 * at half the radius, and a corner that cannot be rounded at all is kept sharp.
 * As with everything else here, the mesh is only ever asked about single points.
 *
 * @param {object|null} nm  a NavMesh instance; null skips the mesh check
 * @param {number[][]} path  [[x,y,z], ...]
 * @param {object} [opts]
 * @param {number} [opts.radius=DEFAULT_CORNER_RADIUS_M]  metres of each leg to
 *   round away. Larger = rounder corners; it does NOT let the route wander.
 * @param {number} [opts.angleTol=1]  degrees below which a turn counts as straight
 * @param {number} [opts.maxDrift]  metres a fillet point may sit from any node;
 *   default curveDrift(nm)
 * @param {number} [opts.maxSamples=16]  samples on the sharpest fillet
 * @returns {number[][]} the smoothed polyline (same endpoints)
 */
export function smoothRoutePath(nm, path, {
  radius = DEFAULT_CORNER_RADIUS_M, angleTol = 1, maxDrift, maxSamples = 16,
  driftCells, driftMaxM,
} = {}) {
  if (!Array.isArray(path) || path.length < 3) return path ? path.map(p => p.slice()) : []
  const drift = maxDrift ?? curveDrift(nm, { driftCells, driftMaxM })
  const onMesh = (p) => !nm || snapToNavmesh(nm, p[0], p[2]).dist <= drift

  const pts = turningPoints(path, { angleTol })
  if (pts.length < 3) return pts // a route with no turn is already the curve

  const out = [pts[0]]
  for (let i = 1; i < pts.length - 1; i++) {
    const a = pts[i - 1], c = pts[i], b = pts[i + 1]
    const ax = a[0] - c[0], az = a[2] - c[2]
    const bx = b[0] - c[0], bz = b[2] - c[2]
    const la = Math.hypot(ax, az), lb = Math.hypot(bx, bz)
    if (la < 1e-9 || lb < 1e-9) { out.push(c); continue }
    const ua = [ax / la, az / la], ub = [bx / lb, bz / lb]

    // How sharp is this turn? A tight corner earns more samples than a gentle
    // one, so a long shallow bend does not carry the cost of a hairpin.
    const turn = Math.acos(Math.min(1, Math.max(-1, -(ua[0] * ub[0] + ua[1] * ub[1]))))
    const n = Math.min(maxSamples, Math.max(2, Math.round((turn * 180 / Math.PI) / 12)))

    // Half the shorter leg: consecutive fillets cannot overlap.
    let r = Math.min(radius, la * 0.5, lb * 0.5)
    let placed = false
    for (let attempt = 0; attempt < 4 && r > 1e-3; attempt++, r *= 0.5) {
      const p0 = [c[0] + ua[0] * r, c[1], c[2] + ua[1] * r]
      const p2 = [c[0] + ub[0] * r, c[1], c[2] + ub[1] * r]
      const arc = quadratic(p0, c, p2, n)
      if (arc.every(onMesh)) { out.push(...arc); placed = true; break }
    }
    if (!placed) out.push(c) // no room to round this one — it stays sharp
  }
  out.push(pts[pts.length - 1])
  return out
}

/**
 * Concatenate an ordered list of navmesh polyline segments ([[x,y,z], …]) into
 * one polyline. Consecutive navmesh segments share their joint node, so each
 * segment after the first drops its leading point when it is XZ-coincident with
 * the current tail (< 1e-6). A null / zero-length segment throws (loud failure —
 * a gap in the route is a planning bug, not something to paper over). An empty
 * `segments` array returns [].
 */
export function concatNavPaths(segments) {
  const out = []
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i]
    if (!seg || seg.length === 0) throw new Error('concatNavPaths: empty segment at index ' + i)
    for (let j = 0; j < seg.length; j++) {
      if (j === 0 && out.length > 0 && distXZ(out[out.length - 1], seg[0]) < 1e-6) continue
      out.push(seg[j])
    }
  }
  return out
}

/**
 * Plan a multi-waypoint route into ONE concatenated polyline (v1: one route ->
 * one kimodo call; per-consecutive-pair planning, then concat + resample once).
 *
 * @param {object|null} nm  a NavMesh instance (required for mode 'navmesh')
 * @param {number[][]} waypoints  [[x,y,z], …], length >= 2
 * @param {object} [opts]
 * @param {'navmesh'|'straight'} [opts.mode='navmesh']  'navmesh' plans each pair
 *   via nm.findPath; 'straight' is an EXPLICIT obstacle-ignoring mode (each
 *   segment is just [a, b]) — never a silent fallback when the navmesh is absent.
 * @param {'none'|'curve'} [opts.smooth='none']  shape of the planned route.
 *   'none' keeps the raw A* polyline; 'curve' rounds each TURN of the whole
 *   concatenated route into a Bézier fillet (smoothRoutePath), waypoint corners
 *   included. Straight runs are left exactly where the navmesh put them.
 * @returns {{ mode:string, smooth:string, path:number[][], segments:Array<{from,to,path,length,rawLength,rawVertices}>, length:number, rawLength:number, waypoints:number[][], snapDrift:number[] }}
 *   `segments` carry the RAW planned polylines; `path` is the smoothed route.
 *   `waypoints` are the requested points, which the route passes through
 *   exactly; `snapDrift[i]` is how far point i sits OFF the mesh, i.e. the
 *   length of the straight leg used to reach it.
 */
export function planWaypointRoute(nm, waypoints, { mode = 'navmesh', smooth = DEFAULT_SMOOTH } = {}) {
  if (!Array.isArray(waypoints) || waypoints.length < 2) {
    throw new Error('planWaypointRoute: need >= 2 waypoints')
  }
  if (mode !== 'navmesh' && mode !== 'straight') {
    throw new Error('planWaypointRoute: unknown mode ' + mode)
  }
  if (mode === 'navmesh' && !nm) {
    throw new Error('planWaypointRoute: navmesh required for mode "navmesh"')
  }
  // A mode we don't know is a caller bug, and silently planning the raw route
  // would look like smoothing that quietly does nothing — the exact failure this
  // module has now had twice. 'pull' lands here since its removal.
  if (!SMOOTH_MODES.includes(smooth)) {
    throw new Error('planWaypointRoute: unknown smooth mode ' + smooth)
  }
  // OFF-MESH POINTS ARE VISITED, NOT REPLACED. nm.findPath snaps its endpoints
  // to the nearest node internally, so a point off the mesh never failed — it
  // planned a route between points that were not the waypoints, and the walk
  // simply started or finished somewhere else. Snapping them explicitly fixed
  // the bookkeeping but kept that behaviour: the character still began at the
  // nearest node rather than where it was standing.
  //
  // So each leg is built as  a -> snap(a) -> [navmesh] -> snap(b) -> b : the
  // route leaves the actual position in a straight line, crosses the mesh
  // properly, and finishes with a straight line to the actual destination. A
  // point already on the mesh contributes no extra vertex, so the common case is
  // unchanged.
  const snapOf = (wp) => {
    if (mode !== 'navmesh') return { p: wp, drift: 0 }
    const { xz, dist } = snapToNavmesh(nm, wp[0], wp[2])
    // Keep the caller's Y: navmesh nodes carry the BAKE's floor height, which can
    // be metres from the world floor the caller works in (a real one measured at
    // -6.2 while the avatar stands at 0).
    return { p: [xz[0], wp[1], xz[1]], drift: Number.isFinite(dist) ? dist : 0 }
  }
  const snapped = waypoints.map(snapOf)
  const snapDrift = snapped.map((s2) => s2.drift)
  // Below this the snap is a rounding error, not an excursion worth a vertex.
  const OFF_MESH_EPS = 1e-3

  const segments = []
  const segPaths = []
  let rawTotal = 0
  for (let i = 0; i < waypoints.length - 1; i++) {
    const a = waypoints[i], b = waypoints[i + 1]
    let rawPath
    if (mode === 'navmesh') {
      const sa = snapped[i], sb = snapped[i + 1]
      const onMesh = nm.findPath(sa.p, sb.p)
      if (!onMesh || onMesh.length === 0) {
        throw new Error(`no navmesh path from waypoint ${i} to waypoint ${i + 1}`)
      }
      // The straight legs on and off the mesh bracket the planned one. Added
      // only when the point is genuinely off it, so an on-mesh route is the
      // polyline it always was.
      rawPath = []
      if (sa.drift > OFF_MESH_EPS) rawPath.push(a)
      for (const pt of onMesh) rawPath.push(pt)
      if (sb.drift > OFF_MESH_EPS) rawPath.push(b)
    } else {
      rawPath = [a, b]
    }
    const rawLength = pathLengthXZ(rawPath)
    rawTotal += rawLength
    segPaths.push(rawPath)
    // Segments stay RAW and are reported that way: they are the planning record
    // (which pair routed where, how long the grid route was), while `path` is
    // the shape that gets walked. Smoothing them individually would also pin
    // every waypoint, which is exactly what we do not want — see below.
    segments.push({ from: a, to: b, path: rawPath, length: rawLength, rawLength, rawVertices: rawPath.length })
  }
  // Smooth the CONCATENATED route, not each segment: a waypoint is an interior
  // corner like any other and gets rounded like any other. Pinning it would put
  // a hard turn in the middle of the walk for no reason the walker can see —
  // the route passes through the waypoints, it does not pivot on them. Only the
  // route's own two ENDPOINTS are held exactly (smoothRoutePath), which is what
  // keeps walk-here landing on the clicked destination.
  const raw = concatNavPaths(segPaths)
  // Diagonal join first, then rounding. Order matters: joining turns a staircase
  // into straight diagonal runs, which leaves the rounding a few real corners to
  // fillet instead of a hundred grid steps — and a fillet on a corner that was
  // only ever a grid artefact is wasted geometry.
  const joined = smooth === 'curve' && mode === 'navmesh'
    ? joinDiagonals(nm, raw)
    : raw
  const path = smooth === 'curve'
    ? smoothRoutePath(mode === 'navmesh' ? nm : null, joined)
    : raw
  return {
    mode, smooth, path, segments,
    length: pathLengthXZ(path), rawLength: pathLengthXZ(raw),
    // The route passes through every requested point, so these ARE the
    // waypoints — pins and per-waypoint distances measure against them.
    // snapDrift[i] is how far point i sits off the mesh: 0 when it is on it,
    // and the length of that leg's straight excursion when it is not.
    waypoints, snapDrift,
  }
}

/**
 * Map each input waypoint to the frame index (0..n-1) of the n-frame arc-length
 * resample nearest to it (so the wizard can report "waypoint k reached at frame
 * f"). Scans polyline vertices monotonically forward from the vertex chosen for
 * the previous waypoint — a self-crossing route can pass near an earlier
 * waypoint again, and without the forward-only scan a later waypoint could map
 * to an earlier frame. Degenerate zero-length path -> all zeros. Path endpoints
 * (always the first/last waypoint here) map to frame 0 and n-1.
 */
export function waypointFrameIndices(path, waypoints, n, profile = null) {
  // Cumulative XZ arc length over the polyline vertices (same loop as resamplePathXZ).
  const seg = [0]
  for (let i = 1; i < path.length; i++) seg[i] = seg[i - 1] + distXZ(path[i - 1], path[i])
  const total = seg[seg.length - 1]
  if (total === 0) return waypoints.map(() => 0)
  const out = []
  let start = 0
  for (let k = 0; k < waypoints.length; k++) {
    const w = waypoints[k]
    let best = start, bestD = Infinity
    for (let i = start; i < path.length; i++) {
      const d = distXZ(path[i], w)
      if (d < bestD) { bestD = d; best = i }
    }
    start = best // monotonic: the next waypoint can't land on an earlier vertex
    // Arc position -> frame. This MUST go through the same profile the resample
    // used, or a pinned waypoint would be anchored at a frame the root does not
    // reach until later (the whole point of easing is that arc and time are no
    // longer proportional).
    const sFrac = seg[best] / total
    out.push(Math.round((profile ? profile.inverse(sFrac) : sFrac) * (n - 1)))
  }
  return out
}

/**
 * Default root2d anchor density: one anchor every 3 generation frames, i.e. ten
 * a second at kimodo's 30 fps.
 *
 * Not every frame. Anchoring all 30 leaves the model no room to put its own gait
 * between the points it is being held to, and the constraint carries FACING as
 * well as position — so a per-frame anchor is also a per-frame heading command.
 * Endpoints and every waypoint frame are anchored whatever the stride, so a
 * sparser one loosens how tightly the path is tracked BETWEEN the points the
 * user placed, never which points get hit.
 */
export const DEFAULT_CONSTRAINT_STRIDE = 3

/**
 * Frames over which the requested heading is eased from the character's CURRENT
 * facing into the route's — 12 at kimodo's 30 fps, i.e. 0.4 s, about how long a
 * person takes to turn on the spot.
 *
 * This is what makes an ANCHORED start (above) compatible with the pose the
 * character is actually in. The constraint carries facing as well as position,
 * so frame 0 asks the character to be turned along the route on the very first
 * frame — a heading the opening body pose may simply not be in, and the model
 * resolves that conflict by twisting rather than by turning. Interpolating from
 * where the character faces gives it frames in which to turn instead.
 *
 * Costs nothing when it is not needed: a character already facing along the
 * route ramps between the same two angles, so the emitted heading is unchanged.
 */
export const DEFAULT_HEADING_RAMP_FRAMES = 12

/**
 * Default root2d CFG scale. Kept low deliberately — the constraint carries BOTH
 * path (position) and facing (heading), and heading is a strong channel; at
 * weight >= 2 it visibly fights the gait.
 */
export const DEFAULT_CONSTRAINT_WEIGHT = 1

/**
 * The range kimodo accepts for `constraint_weight`: `0 < x <= 20`
 * (`service/SPEC.md`). ZERO IS NOT VALID — it is a 422, not an unguided
 * generation, which is what an "ignore the path entirely" reading of the knob
 * would have produced. The A/B for unguided motion is `heading: false` plus no
 * constraints, or `__GEN_BODY_MOTION__`, not weight 0.
 */
export const MIN_CONSTRAINT_WEIGHT = 0.01
export const MAX_CONSTRAINT_WEIGHT = 20

/** Clamp a caller's weight into the range kimodo will accept. */
export function clampConstraintWeight(w) {
  const n = Number(w)
  if (!Number.isFinite(n)) return DEFAULT_CONSTRAINT_WEIGHT
  return Math.min(MAX_CONSTRAINT_WEIGHT, Math.max(MIN_CONSTRAINT_WEIGHT, n))
}

/**
 * The frame indices a root2d constraint anchors, for an n-frame clip: every
 * `stride`th frame, plus the last frame, plus every pinned frame (waypoints),
 * sorted + deduped. Split out of buildRoot2dConstraint so the wizard can report
 * the anchor count for a stride the user is typing — before any resample or
 * network call exists to build a constraint from.
 *
 * A stride is rounded and floored at 1: a fractional or zero stride would either
 * skip frames unevenly or loop forever, and silently accepting one would report
 * an anchor count the request never used.
 *
 * @param {number} n  frame count (>= 1)
 * @param {object} [opts]
 * @param {number} [opts.stride=DEFAULT_CONSTRAINT_STRIDE]  anchor every N frames
 * @param {number[]} [opts.pinFrames=[]]  extra indices, clamped to [0, n-1]
 * @returns {number[]} ascending, deduped frame indices
 */
export function constraintFrames(n, { stride = DEFAULT_CONSTRAINT_STRIDE, pinFrames = [] } = {}) {
  const step = Math.max(1, Math.round(stride))
  const frameSet = new Set()
  for (let f = 0; f < n; f += step) frameSet.add(f)
  frameSet.add(n - 1)
  for (const p of pinFrames) frameSet.add(Math.min(n - 1, Math.max(0, p)))
  return [...frameSet].sort((a, b) => a - b)
}

/**
 * Frames averaged when smoothing the requested heading — 15 at kimodo's 30 fps,
 * half a second, the same window the pelvis-yaw extraction uses.
 *
 * The tangent of a resampled polyline is jittery even when the polyline is
 * smooth. Measured on the Startup_Level bake (real 8-connected navmesh, 783
 * nodes at 0.2 m), reading the heading back off the finished request:
 *
 *   window   step between anchors   curvature   turn reversals (long route)
 *      0            38 deg            53 deg          3
 *      5            25                31              3
 *      9            15                21              3
 *     15            11                11              0
 *
 * Anchors are 3 frames apart, so 38 degrees between them is about 380 deg/s —
 * and the direction of turn REVERSING several times along one route is not the
 * route bending, it is noise. Asking a walking model to reverse its turn every
 * tenth of a second is the kind of demand it answers by twisting rather than
 * turning.
 *
 * The cost of a wider window is that a genuine corner is spread over it: at 15
 * frames a hard turn is taken over half a second, roughly 0.65 m at a walk,
 * which is about what a person does anyway. Wider than that and the heading
 * starts to lag the path it is supposed to describe.
 */
export const DEFAULT_HEADING_SMOOTH_FRAMES = 15

/**
 * Smooth a series of angles, preserving the LAST value exactly.
 *
 * Unwrapped first, so a route crossing the +/-pi seam averages the way it turns
 * rather than snapping a full circle. The final value is pinned because it is
 * the facing the walk ends on — the pose lock and the committed group yaw are
 * both derived from it, so blurring it would leave the idle pointing somewhere
 * the animation never did. The correction is ramped in, never stepped.
 *
 * The START is deliberately NOT pinned: the opening frames are overwritten by
 * the heading ramp anyway, which eases from where the character actually faces.
 */
function smoothAngles(angles, window) {
  const n = angles.length
  const half = Math.max(0, Math.floor(window / 2))
  if (half === 0 || n < 3) return angles
  const out = new Float64Array(n)
  for (let i = 0; i < n; i++) {
    let sum = 0
    for (let k = -half; k <= half; k++) sum += angles[Math.min(n - 1, Math.max(0, i + k))]
    out[i] = sum / (half * 2 + 1)
  }
  const bias = angles[n - 1] - out[n - 1]
  if (Math.abs(bias) > 1e-12) for (let i = 0; i < n; i++) out[i] += bias * (i / (n - 1))
  return out
}

/**
 * Rotate an XZ vector by `a` radians about Y, in THREE's convention
 * (x' = x cos + z sin, z' = -x sin + z cos).
 *
 * The convention matters and is easy to get backwards: a group yaw of theta
 * points the character's local +z along world (sin theta, cos theta), so
 * world = rotXZ(local, +theta) and local = rotXZ(world, -theta).
 */
export function rotXZ(x, z, a) {
  if (!a) return [x, z]
  const c = Math.cos(a), s = Math.sin(a)
  return [x * c + z * s, -x * s + z * c]
}

/**
 * Build the kimodo `root2d` constraint (position + optional heading) from an
 * n-frame resampled [x,z] root path. Extracted verbatim from genBodyMotionOnNavmesh
 * so the waypoint path can reuse it unchanged.
 *
 * Kimodo generation is ORIGIN-ROOTED: the root track always starts at ~(0,0)
 * regardless of the constraint, so absolute world waypoints make the model
 * burn the clip sprinting from the origin to catch the path (measured: a 3m
 * walk requested at (5,-3) realizes as a 9.6m dash from (0,0)). Re-reference
 * the constraint to start at (0,0) and translate the result back afterwards
 * (the caller adds originX/originZ back onto the realized root track).
 *
 * @param {number[][]} rootXZ  n-frame resampled [x,z] path (resamplePathXZ output)
 * @param {object} [opts]
 * @param {number} [opts.stride=DEFAULT_CONSTRAINT_STRIDE]  send an anchor every N frames (endpoints
 *   always included). Sparse anchors let the model interpolate its own natural
 *   motion between them (smoother facing); dense (1) tracks the path tightest.
 * @param {boolean} [opts.heading=true]  include the facing channel (false omits it).
 * @param {number[]} [opts.pinFrames=[]]  extra frame indices to always anchor
 *   (clamped to [0, n-1], merged + deduped against the stride hits + endpoint).
 * @param {number[]|null} [opts.startFacing=null]  the character's CURRENT world
 *   facing [fx, fz]. With it, the requested heading is eased from that facing
 *   into the path's over `headingRampFrames` instead of demanding the route's
 *   direction from the first anchor — see the ramp note below.
 * @param {number} [opts.headingRampFrames=DEFAULT_HEADING_RAMP_FRAMES]  length
 *   of that ease-in, in frames. 0 disables it.
 * @param {number} [opts.headingSmoothFrames=DEFAULT_HEADING_SMOOTH_FRAMES]
 *   low-pass applied to the requested heading. 0 sends the raw tangent.
 * @param {number} [opts.frameYaw=0]  the avatar group's yaw. Positions and
 *   headings are rotated into that frame LAST, because the generated clip plays
 *   inside the group. Everything before that — `startFacing`, the ramp, the path
 *   tangents — stays in world, so the two are never mixed.
 * @returns {{ constraint:{type:'root2d',frames:number[],root_xz:number[][],heading?:number[][]}, originX:number, originZ:number }}
 */
export function buildRoot2dConstraint(rootXZ, {
  stride = DEFAULT_CONSTRAINT_STRIDE, heading = true, pinFrames = [],
  startFacing = null, headingRampFrames = DEFAULT_HEADING_RAMP_FRAMES, frameYaw = 0,
  headingSmoothFrames = DEFAULT_HEADING_SMOOTH_FRAMES,
} = {}) {
  const n = rootXZ.length
  // The origin re-reference is read from rootXZ[0] whether or not frame 0 is
  // anchored: kimodo is origin-rooted regardless, so the constraint's positions
  // are still expressed relative to where the route starts.
  const [originX, originZ] = rootXZ[0]
  const frames = constraintFrames(n, { stride, pinFrames })
  // THE REQUEST IS IN THE CHARACTER'S FRAME, NOT THE WORLD'S. Kimodo's output is
  // played INSIDE the avatar group, so it comes out rotated by whatever yaw that
  // group carries. A world-referenced constraint is therefore correct only while
  // that yaw is zero — and it stops being zero the moment a generated walk turns
  // the character, which is why the SECOND walk-here click came out along a
  // rotated path and the third more so. Rotating the route into the group's
  // frame here is what makes the two agree; the realized root is rotated back
  // on the way out.
  const constraint = {
    type: 'root2d',
    frames,
    root_xz: frames.map(f => rotXZ(rootXZ[f][0] - originX, rootXZ[f][1] - originZ, -frameYaw)),
  }
  // Face along the path. Kimodo's `heading` is the HIP-LINE angle
  // θ = atan2(hip.z, -hip.x) (kimodo compute_heading_angle), not the travel
  // direction — for a desired facing (fx, fz) that works out to
  // θ = atan2(fx, fz), i.e. heading [cos,sin] = [fz, fx]: the path tangent
  // with its components SWAPPED (measured empirically via FK of generated
  // walks; the unswapped tangent asks for a 90°-off facing and the CFG
  // conflict visibly twists the body). Translation-invariant, so the origin
  // re-reference doesn't affect it. Opt out with { heading: false }.
  if (heading !== false) {
    const tan = pathTangents(rootXZ)
    // The tangent of a resampled polyline is jittery even when the polyline is
    // smooth, so the heading is low-passed before anything else is done to it —
    // BEFORE the ramp, so the ramp's ease out of the character's real facing
    // stays exact, and with the last value pinned so the walk still finishes
    // facing where it finishes.
    const wrapped = new Float64Array(n)
    let prevA = 0
    for (let f = 0; f < n; f++) {
      let a2 = Math.atan2(tan[f][0], tan[f][1])
      while (a2 - prevA > Math.PI) a2 -= Math.PI * 2
      while (a2 - prevA < -Math.PI) a2 += Math.PI * 2
      wrapped[f] = a2
      prevA = a2
    }
    const pathAngle = smoothAngles(wrapped, headingSmoothFrames)
    // Ease the demand in. Freeing frame 0 was not enough on its own: with a
    // stride of 3 the FIRST anchor is frame 3, so a character standing at 90 deg
    // to the route was still being told to face along it a tenth of a second
    // later — a heading the opening body pose cannot be in, which is a conflict
    // the model resolves by twisting rather than by turning. Interpolating from
    // where the character actually faces gives it frames in which to turn.
    //
    // Interpolated as an ANGLE along the shortest arc, not as a lerp of the two
    // vectors: a vector lerp shortens toward zero as the two near opposition
    // (undefined at exactly 180 deg) and sweeps at a non-constant rate. Since
    // 180 deg is precisely the case this exists for — being asked to walk back
    // the way you face — the vector form fails where it is needed most.
    //
    // Self-disabling: a character already facing along the route ramps from and
    // to the same angle, so the emitted heading is unchanged.
    const ramp = Math.max(0, Math.round(headingRampFrames))
    const from = startFacing && Math.hypot(startFacing[0], startFacing[1]) > 1e-9
      ? Math.atan2(startFacing[0], startFacing[1])
      : null
    constraint.heading = frames.map(f => {
      let a = pathAngle[f]
      if (from != null && ramp > 0 && f < ramp) {
        const t = f / ramp
        a = from + shortestAngleTo(from, a) * t
      }
      const fx = Math.sin(a), fz = Math.cos(a)
      // Into the character's frame, like the positions above.
      const [gx, gz] = rotXZ(fx, fz, -frameYaw)
      // The empirical component swap (see above): facing (fx, fz) -> [fz, fx].
      return [gz, gx]
    })
  }
  return { constraint, originX, originZ }
}

/** Signed shortest angular distance from `a` to `b`, in (-pi, pi]. */
function shortestAngleTo(a, b) {
  let d = (b - a) % (Math.PI * 2)
  if (d > Math.PI) d -= Math.PI * 2
  if (d <= -Math.PI) d += Math.PI * 2
  return d
}

/** kimodo's SOMA models are fixed 30 fps (service/SPEC.md). */
export const FPS = 30

/**
 * Everything between a planned route and the `root2d` constraint that describes
 * it: resample at the speed profile, pin the waypoint frames through that same
 * profile, choose the anchors, ease the heading out of the character's current
 * facing.
 *
 * ONE function so the request and its on-screen preview cannot drift. The
 * preview is only worth drawing if it shows what is actually sent, and the two
 * were previously going to be separate reconstructions of the same six steps.
 *
 * @param {number[][]} routePath  the planned (already smoothed) polyline
 * @param {number[][]} waypoints  the user's own points, pinned as anchors
 * @param {object} opts
 * @param {number} opts.durationSec
 * @param {number} [opts.fps=FPS]
 * @param {number} [opts.stride=DEFAULT_CONSTRAINT_STRIDE]
 * @param {boolean} [opts.heading=true]
 * @param {number[]|null} [opts.startFacing=null]
 * @param {number} [opts.headingRampFrames=DEFAULT_HEADING_RAMP_FRAMES]
 * @param {number} [opts.easeInSec=DEFAULT_EASE_IN_SEC]
 * @param {number} [opts.easeOutSec=DEFAULT_EASE_OUT_SEC]
 * @returns {{constraint:object, originX:number, originZ:number, nFrames:number,
 *   rootXZ:number[][], pinFrames:number[], profile:object}}
 */
export function buildRouteConstraint(routePath, waypoints, {
  durationSec, fps = FPS,
  stride = DEFAULT_CONSTRAINT_STRIDE, heading = true,
  startFacing = null, headingRampFrames = DEFAULT_HEADING_RAMP_FRAMES,
  easeInSec = DEFAULT_EASE_IN_SEC, easeOutSec = DEFAULT_EASE_OUT_SEC, frameYaw = 0,
  headingSmoothFrames = DEFAULT_HEADING_SMOOTH_FRAMES,
} = {}) {
  const nFrames = Math.max(2, Math.round(durationSec * fps))
  const profile = speedProfileFor(durationSec, { easeInSec, easeOutSec })
  const rootXZ = resamplePathXZ(routePath, nFrames, profile)
  // The SAME profile, or a pinned waypoint lands on a frame the root has not
  // reached yet.
  const pinFrames = waypointFrameIndices(routePath, waypoints, nFrames, profile)
  const built = buildRoot2dConstraint(rootXZ, {
    stride: Math.max(1, Math.round(stride)),
    heading,
    pinFrames,
    startFacing,
    headingRampFrames,
    frameYaw,
    headingSmoothFrames,
  })
  return { ...built, nFrames, rootXZ, pinFrames, profile, frameYaw }
}

/**
 * The facings a route will actually request, as world points with a direction —
 * for drawing the heading on the path preview.
 *
 * Derived from the constraint itself rather than recomputed, so what is drawn is
 * what is sent. Heading is UN-SWAPPED back to a facing here: kimodo is given the
 * hip-line form `[fz, fx]` (see buildRoot2dConstraint), and an arrow drawn from
 * that directly would point 90 degrees off — which is exactly the mistake the
 * swap exists to prevent, and would make a correct request look broken.
 *
 * `every` subsamples the anchors; 1 (the default) draws every root point the
 * request actually carries, which is the point of the overlay — the anchors ARE
 * the request, and a subsampled picture of them can hide exactly the frames
 * where the heading and the path disagree.
 *
 * @param {number[][]} routePath
 * @param {number[][]} waypoints
 * @param {object} opts  as buildRouteConstraint, plus `every`
 * @returns {Array<{x:number, z:number, fx:number, fz:number, frame:number}>}
 */
export function routeHeadingPreview(routePath, waypoints, { every = 1, ...opts } = {}) {
  if (!Array.isArray(routePath) || routePath.length < 2) return []
  const { constraint, rootXZ, originX, originZ, frameYaw } = buildRouteConstraint(routePath, waypoints, opts)
  if (!constraint.heading) return []
  const step = Math.max(1, Math.round(every))
  const out = []
  for (let i = 0; i < constraint.frames.length; i += step) {
    const f = constraint.frames[i]
    const [hz, hx] = constraint.heading[i]
    // Back to WORLD for drawing: the stored heading is in the character's frame
    // and in kimodo's hip-line form, so it needs both the un-swap and the
    // un-rotate or every arrow is drawn turned by the group's yaw.
    const [wx, wz] = rotXZ(hx, hz, frameYaw)
    out.push({
      // rootXZ is in world; the constraint's own root_xz is origin-relative.
      x: rootXZ[f][0], z: rootXZ[f][1], fx: wx, fz: wz, frame: f,
    })
  }
  // Always include the final anchor, whatever the subsampling lands on — the
  // facing the walk ends on is the one a reader most wants to check.
  const last = constraint.frames.length - 1
  if (out.length === 0 || out[out.length - 1].frame !== constraint.frames[last]) {
    const f = constraint.frames[last]
    const [hz, hx] = constraint.heading[last]
    const [wx, wz] = rotXZ(hx, hz, frameYaw)
    out.push({ x: rootXZ[f][0], z: rootXZ[f][1], fx: wx, fz: wz, frame: f })
  }
  void originX; void originZ
  return out
}
